import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Phone, Pencil, Trash2 } from "lucide-react";

const STAGES = ["new", "contacted", "qualified", "quoted", "won", "lost"];
const SOURCES = ["website", "referral", "outbound", "tender", "walk_in", "phone", "email"];

const EMPTY_LEAD = {
  name: "",
  company: "",
  phone: "",
  email: "",
  city: "",
  expected_monthly_litres: 0,
  source: "outbound",
  notes: "",
};

export default function Leads() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const role = user?.role;
  const canDelete = ["admin", "accounts", "manager"].includes(role);

  const { data } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.get("/leads").then((r) => r.data),
    refetchInterval: 30000,
  });

  const [editor, setEditor] = useState(null); // {mode:'create'|'edit', lead}
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [convert, setConvert] = useState(null);
  const [quote, setQuote] = useState(null);
  const [rate, setRate] = useState(95);

  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/leads/${id}`, patch),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });

  const create = useMutation({
    mutationFn: (payload) => api.post("/leads", payload),
    onSuccess: () => {
      toast.success("Lead added");
      setEditor(null);
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to add lead"),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/leads/${id}`),
    onSuccess: () => {
      toast.success("Lead deleted");
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const doConvert = useMutation({
    mutationFn: ({ id, price_per_litre }) =>
      api.post(`/leads/${id}/convert`, { price_per_litre, credit_limit: 100000, credit_days: 15 }),
    onSuccess: (r) => {
      toast.success(`Converted to customer ${r.data.customer_code}`);
      setConvert(null);
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const byStage = STAGES.map((s) => ({
    stage: s,
    items: (data?.items || []).filter((l) => l.status === s),
  }));

  const openCreate = () => setEditor({ mode: "create", lead: { ...EMPTY_LEAD } });
  const openEdit = (lead) =>
    setEditor({
      mode: "edit",
      lead: {
        id: lead.id,
        name: lead.name || "",
        company: lead.company || "",
        phone: lead.phone || "",
        email: lead.email || "",
        city: lead.city || "",
        expected_monthly_litres: lead.expected_monthly_litres || 0,
        source: lead.source || "outbound",
        notes: lead.notes || "",
      },
    });

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Sales Pipeline</h1>
          <p className="text-sm text-muted-foreground">
            {data?.total || 0} leads across the funnel · Front-page inquiries added automatically
          </p>
        </div>
        <Button onClick={openCreate} data-testid="lead-new-btn">
          <Plus size={14} className="mr-1.5" /> New lead
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {byStage.map(({ stage, items }) => (
          <Card key={stage} className="flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {stage.replace("_", " ")}
                </div>
                <div className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                  {items.length}
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 space-y-2 pt-0">
              {items.map((l) => (
                <div
                  key={l.id}
                  className="rounded border border-border/60 p-2 text-xs"
                  data-testid={`lead-card-${l.code}`}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className="flex-1">
                      <div className="font-semibold">{l.company || l.name}</div>
                      <div className="text-muted-foreground">{l.name}</div>
                    </div>
                    <div className="flex flex-shrink-0 gap-0.5">
                      <button
                        onClick={() => openEdit(l)}
                        className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title="Edit lead"
                        data-testid={`lead-edit-${l.code}`}
                      >
                        <Pencil size={12} />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => setConfirmDelete(l)}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Delete lead"
                          data-testid={`lead-delete-${l.code}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Phone size={10} /> {l.phone}
                  </div>
                  {l.source && (
                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                      via {l.source.replace("_", " ")}
                    </div>
                  )}
                  {l.expected_monthly_litres > 0 && (
                    <div className="mt-1 text-[10px]">
                      ~ {l.expected_monthly_litres.toLocaleString("en-IN")} L/mo
                    </div>
                  )}
                  {l.quoted_rate > 0 && (
                    <div className="mt-1 text-[10px] text-primary">Quoted ₹{l.quoted_rate}/L</div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {STAGES.filter((s) => s !== l.status && s !== "won").map((s) => (
                      <button
                        key={s}
                        onClick={() => update.mutate({ id: l.id, patch: { status: s } })}
                        className="rounded border border-border/60 px-1.5 py-0.5 text-[9px] uppercase text-muted-foreground hover:border-primary hover:text-primary"
                        data-testid={`move-${l.code}-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                    {l.status === "quoted" && (
                      <button
                        onClick={() => setConvert(l)}
                        className="rounded bg-status-delivered px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary-foreground"
                        data-testid={`convert-${l.code}`}
                      >
                        Convert
                      </button>
                    )}
                    {l.status !== "quoted" && l.status !== "won" && (
                      <button
                        onClick={() => {
                          setQuote(l);
                          setRate(l.quoted_rate || 95);
                        }}
                        className="rounded border border-primary px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary hover:bg-primary hover:text-primary-foreground"
                        data-testid={`quote-${l.code}`}
                      >
                        Quote
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <div className="py-4 text-center text-xs text-muted-foreground">Empty</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create / Edit dialog */}
      <LeadEditorDialog
        open={!!editor}
        editor={editor}
        onClose={() => setEditor(null)}
        onSave={(form) => {
          if (editor.mode === "create") {
            create.mutate(form);
          } else {
            update.mutate({ id: editor.lead.id, patch: form });
            setEditor(null);
          }
        }}
        saving={create.isPending || update.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete lead?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDelete?.company || confirmDelete?.name}</b> ({confirmDelete?.code}) will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="lead-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quote dialog */}
      <Dialog open={!!quote} onOpenChange={(v) => !v && setQuote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send quote to {quote?.company || quote?.name}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Rate (₹ / L)</Label>
            <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} />
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                update.mutate({ id: quote.id, patch: { quoted_rate: rate, status: "quoted" } });
                setQuote(null);
              }}
              data-testid="btn-send-quote"
            >
              Save quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={!!convert} onOpenChange={(v) => !v && setConvert(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert lead to customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              A new customer profile will be created for <b>{convert?.company || convert?.name}</b>.
            </p>
            <div>
              <Label>Price per litre</Label>
              <Input type="number" step="0.1" defaultValue={convert?.quoted_rate || 95} id="conv-rate" />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() =>
                doConvert.mutate({
                  id: convert.id,
                  price_per_litre: Number(document.getElementById("conv-rate").value) || 95,
                })
              }
              data-testid="btn-confirm-convert"
            >
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Volume period → per-month multiplier. 'custom' stores the raw value with no conversion. */
const PERIOD_TO_MONTH = { day: 30, week: 4.33, month: 1, custom: 1 };
const PERIOD_LABEL = { day: "Per day", week: "Per week", month: "Per month", custom: "Custom / other" };

/* ─────────── Editor dialog (create + edit share the same form) ─────────── */
function LeadEditorDialog({ open, editor, onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [volInput, setVolInput] = useState("0");
  const [volPeriod, setVolPeriod] = useState("month");

  React.useEffect(() => {
    if (editor?.lead) {
      setForm(editor.lead);
      // On edit, we only know monthly litres; default the input to month view.
      setVolInput(String(editor.lead.expected_monthly_litres || 0));
      setVolPeriod("month");
    } else {
      setVolInput("0");
      setVolPeriod("month");
    }
  }, [editor]);

  // Live preview: what will actually get stored (litres/month)
  const monthlyLitres = React.useMemo(() => {
    const n = Number(String(volInput).replace(/[^0-9.]/g, "")) || 0;
    return Math.round(n * (PERIOD_TO_MONTH[volPeriod] || 1));
  }, [volInput, volPeriod]);

  if (!editor) return null;
  const mode = editor.mode;
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    const payload = {
      name: form.name,
      company: form.company || "",
      phone: form.phone,
      email: form.email || "",
      city: form.city || "",
      expected_monthly_litres: monthlyLitres,
      source: form.source || "outbound",
      notes: form.notes || "",
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add new lead" : "Edit lead"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="lead-editor-form">
          <div>
            <Label>Contact name *</Label>
            <Input value={form.name} onChange={(e) => setF("name", e.target.value)} required data-testid="lead-form-name" />
          </div>
          <div>
            <Label>Company</Label>
            <Input value={form.company} onChange={(e) => setF("company", e.target.value)} data-testid="lead-form-company" />
          </div>
          <div>
            <Label>Phone *</Label>
            <Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} required data-testid="lead-form-phone" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="lead-form-email" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => setF("city", e.target.value)} data-testid="lead-form-city" />
          </div>
          <div className="md:col-span-2">
            <Label>Expected volume</Label>
            <div className="mt-1 grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[2fr_1fr]">
              <Input
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="0"
                value={volInput}
                onChange={(e) => setVolInput(e.target.value)}
                data-testid="lead-form-volume"
              />
              <Select value={volPeriod} onValueChange={setVolPeriod}>
                <SelectTrigger data-testid="lead-form-volume-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">L per day</SelectItem>
                  <SelectItem value="week">L per week</SelectItem>
                  <SelectItem value="month">L per month</SelectItem>
                  <SelectItem value="custom">L (custom)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {volPeriod === "custom"
                ? `Stored as-is: ${monthlyLitres.toLocaleString("en-IN")} L`
                : `≈ ${monthlyLitres.toLocaleString("en-IN")} L / month (${PERIOD_LABEL[volPeriod]})`}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Source</Label>
            <Select value={form.source} onValueChange={(v) => setF("source", v)}>
              <SelectTrigger data-testid="lead-form-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setF("notes", e.target.value)} data-testid="lead-form-notes" />
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="lead-form-save">
              {saving ? "Saving…" : mode === "create" ? "Add lead" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
